import { Box, Button, Grid, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { formatDate } from '@roc/feature-utils';
import { Paper, PaperBorderType } from '@roc/ui';
import { observer } from 'mobx-react';
import { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { WireInstructionsStore } from '../stores/wireInstructionsStore';

const TERMS: { title: string; text: string }[] = [
  {
    title: 'Title Company Representations & Warranties.',
    text: 'By completing, signing, and returning this form, the Title Company represents and warrants to Lender that: (a) it is duly licensed and in good standing as a title insurer, settlement agent, or escrow agent under the laws of the applicable state; (b) the wire instructions provided herein are accurate, current, and direct to an escrow or trust account maintained exclusively for the purpose of holding real estate closing funds; (c) the designated account is not commingled with the Title Company’s operating funds; (d) the signatory is authorized to provide wire instructions on behalf of the Title Company; and (e) the Title Company has implemented reasonable anti-fraud controls in connection with the delivery and handling of wire instructions.',
  },
  {
    title: 'Prohibition on Unauthorized Changes.',
    text: 'Wire instructions, once submitted and verified pursuant to Section 4, shall not be modified, amended, or superseded without a new completed and signed Wire Instruction Authorization Form. Any purported modification communicated solely by email or facsimile, without a new executed form and re-verification pursuant to Section 4, shall be deemed unauthorized and void. Lender shall not be liable for wire transfers made in reasonable reliance on previously verified instructions that were fraudulently altered without Lender’s knowledge.',
  },
  {
    title: 'Funds Receipt Deadline.',
    text: 'Loan proceeds will be wired to the Title Company only upon satisfaction of all conditions to closing as set forth in the loan commitment and closing instructions.',
  },
  {
    title: 'Returned or Misdirected Wires.',
    text: 'In the event loan proceeds are wired to an incorrect account due to erroneous or fraudulent wire instructions provided by the Title Company, the Title Company shall bear full responsibility for recovering and returning such funds to Lender and shall indemnify, defend, and hold harmless Lender from any losses, costs, and expenses arising therefrom, including reasonable attorneys’ fees.',
  },
  {
    title: 'Wire Recall Cooperation.',
    text: 'In the event of a suspected fraudulent or erroneous wire, both parties shall cooperate fully and immediately with each other, their respective financial institutions, and law enforcement to initiate a wire recall. Neither party waives any rights or remedies by cooperating in a recall effort.',
  },
  {
    title: 'Escrow Account Compliance.',
    text: 'The Title Company represents that the designated escrow or trust account complies with all applicable state laws governing the holding, handling, and disbursement of real estate closing funds, including without limitation any requirements regarding interest-bearing accounts, FDIC insurance coverage, and record-keeping.',
  },
  {
    title: 'Indemnification.',
    text: 'The Title Company shall indemnify, defend, and hold harmless Lender and its officers, directors, employees, and agents from and against any and all claims, losses, damages, liabilities, costs, and expenses (including reasonable attorneys’ fees) arising out of or relating to: (a) any inaccuracy in the wire instructions provided herein; (b) any unauthorized modification of wire instructions; (c) the Title Company’s failure to comply with the verification protocol in Section 4; or (d) any breach of the representations and warranties made herein.',
  },
  {
    title: 'Confidentiality.',
    text: 'The wire instructions contained in this form are confidential. The Title Company shall not disclose the contents of this form to any party other than those with a need to know in connection with the closing of the above-referenced loan transaction, and shall take reasonable steps to protect this information from unauthorized access or disclosure.',
  },
  {
    title: 'No Third-Party Beneficiaries.',
    text: 'This form is for the sole benefit of Lender and Title Company and does not create any rights in any third party, including without limitation the borrower(s), seller(s), or any real estate agent.',
  },
  {
    title: 'Governing Law; Dispute Resolution.',
    text: 'This form and any dispute arising hereunder shall be governed by and construed in accordance with the laws of the State of New York, without regard to its conflict of law principles. Any dispute not resolved informally shall be submitted to binding arbitration in New York under the rules of the American Arbitration Association.',
  },
  {
    title: 'Entire Agreement.',
    text: 'This form, together with Lender’s closing instructions issued in connection with the above-referenced loan, constitutes the entire agreement between the parties with respect to the subject matter hereof and supersedes all prior communications regarding wire transfer instructions for this transaction.',
  },
];

const useStyles = makeStyles((theme: Theme) => ({
  sectionTitle: {
    fontWeight: 600,
  },
  divider: {
    width: '100%',
    height: '1px',
    backgroundColor: '#ddd',
    marginTop: theme.spacing(1),
  },
  list: {
    marginLeft: theme.spacing(2),
    paddingLeft: theme.spacing(1),
  },
  listItem: {
    marginBottom: theme.spacing(1.5),
  },
  termTitle: {
    fontStyle: 'normal',
    fontWeight: 600,
  },
  sigCanvas: {
    width: '100%',
    height: 200,
    background: '#f7f7f7',
    border: '1px solid #aaa',
    borderRadius: 16,
  },
  submitButton: {
    width: 160,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

interface Props {
  store: WireInstructionsStore;
}

export const SignatureStep = observer(({ store }: Props) => {
  const classes = useStyles();
  const sigCanvas = useRef<any>(null);
  const [signed, setSigned] = useState(false);

  const handleSubmit = () => {
    if (!signed || sigCanvas.current?.isEmpty()) {
      return;
    }
    const signature = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
    store.submitWireInstructions(signature);
  };

  return (
    <Box pb={2}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5" className={classes.sectionTitle}>
            Terms, Conditions &amp; Legal Disclosures
          </Typography>
          <Box className={classes.divider} />
        </Grid>
        <Grid item xs={12}>
          <Paper
            variant="outlined"
            style={{ maxHeight: 300, overflowY: 'auto', padding: 24, borderColor: '#ccc' }}
            borderType={PaperBorderType.SOLID_BORDER}
          >
            <Typography variant="body1" component="div" style={{ fontStyle: 'italic' }}>
              <ol className={classes.list}>
                {TERMS.map(term => (
                  <li key={term.title} className={classes.listItem}>
                    <span className={classes.termTitle}>{term.title}</span> {term.text}
                  </li>
                ))}
              </ol>
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Box mt={2}>
            <Typography variant="h5" className={classes.sectionTitle}>
              Authorized Signature
              <Box component="span" color="red" ml={0.5}>
                *
              </Box>
            </Typography>
            <Box className={classes.divider} />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <SignatureCanvas
            penColor="green"
            ref={sigCanvas}
            canvasProps={{ className: classes.sigCanvas }}
            onEnd={() => setSigned(true)}
          />
          <Box display="flex" justifyContent="space-between" width="100%" mt={1}>
            <Button
              size="small"
              onClick={() => {
                sigCanvas.current?.clear();
                setSigned(false);
              }}
            >
              Clear
            </Button>
            <Box color="blue">{formatDate(new Date())}</Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center">
            <Button
              variant="contained"
              color="primary"
              className={classes.submitButton}
              onClick={handleSubmit}
              disabled={!signed || sigCanvas.current?.isEmpty()}
            >
              SUBMIT
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
});
